import { IInventoryPurchaseOrderProductSearchResponseData } from "@/api_framework/api_modals/Inventory";
import Input from "@/app/components/ui/Inputs/Input";
import { useInventoryProductSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryProductSearchAPI";
import { usePRFCreateAPI } from "@/app/hooks/api_hooks/Inventory/usePRFCreateAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Combobox, Transition } from "@headlessui/react";
import { useDebounce } from "@uidotdev/usehooks";
import { Fragment, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";

interface PRFVariant {
  vetic_variant_id: string;
  variant_name: string;
  quantity: number;
}

interface PRFFormValues {
  variants: PRFVariant[];
}

const MIN_SEARCH_CHARS = 3;
const DEBOUNCE_MS = 400;

const ProductRequirementForm = () => {

  const user = useAppSelector((state) => state.auth.user);

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const { execute: submitPRF, isSubmitting } = usePRFCreateAPI();
  const { searchResults, execute: runSearch, clearSearchResults } = useInventoryProductSearchAPI();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedQuery.length >= MIN_SEARCH_CHARS) {
      runSearch(debouncedQuery, undefined, undefined, undefined, mappedClinicId);
    } else {
      clearSearchResults();
    }
  }, [debouncedQuery]);

  const formHook = useForm<PRFFormValues>({
    mode: "onChange",
    defaultValues: { variants: [] },
  });

  const { fields, prepend, remove } = useFieldArray({
    control: formHook.control,
    name: "variants",
  });

  const handleSelect = (items: IInventoryPurchaseOrderProductSearchResponseData[]) => {
    const newItem = items.find((item) => !fields.some((f) => f.vetic_variant_id === item.vetic_variant_id));
    if (newItem) {
      prepend({ vetic_variant_id: newItem.vetic_variant_id, variant_name: newItem.variant_name, quantity: 1 });
    }
  };

  const onSubmit = async (data: PRFFormValues) => {
    if (!mappedClinicId) {
      toast.error("Clinic not selected. Please select a clinic first.");
      return;
    }
    const payload: any = {
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      variants: data.variants.map((v) => ({
        vetic_variant_id: v.vetic_variant_id,
        variant_name: v.variant_name,
        quantity: Number(v.quantity),
      })),
    };
    const response = await submitPRF(payload);
    if (response?.status) {
      formHook.reset({ variants: [] });
      setQuery("");
      clearSearchResults();
    }
  };

  return (
    <div className="bg-white px-4 p-4 rounded-md">
      {/* Header */}
      <h2 className="flex justify-start items-center gap-x-3 border-gray-200 pb-3 border-b font-semibold text-sm">
        Product Requirement Form
      </h2>

      {/* Info section */}
      <div className="bg-[#F9FAFB] rounded-xl px-4 py-3 mt-4 flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Clinic:</span>
          <span className="text-xs font-semibold text-[#1F1F23]">{mappedClinicName || "—"}</span>
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Raised By:</span>
          <span className="text-xs font-semibold text-[#1F1F23]">{user?.displayName || user?.email || "—"}</span>
        </div>
      </div>

      <form onSubmit={formHook.handleSubmit(onSubmit)} className="mt-5 pb-20">
        {/* Multi-select product search */}
        <div className="w-1/2">
          <p className="text-xs text-gray-600 font-medium mb-1.5">Search &amp; Add Products</p>
          <Combobox value={fields as any} onChange={handleSelect} multiple>
            <div className="relative">
              <div className="relative border border-gray-200 bg-white shadow-sm rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 w-full overflow-hidden">
                <div className="flex items-center">
                  {query.length >= MIN_SEARCH_CHARS ? (
                    <button
                      type="button"
                      className="p-2 shrink-0"
                      onClick={() => { setQuery(""); clearSearchResults(); }}
                    >
                      <XMarkIcon className="w-4 h-4 text-red-500" />
                    </button>
                  ) : (
                    <span className="p-2 shrink-0">
                      <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
                    </span>
                  )}
                  <Combobox.Input
                    autoComplete="off"
                    displayValue={() => query}
                    placeholder="Search products (min 3 characters)..."
                    className="py-2 pr-8 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <Combobox.Button className="absolute right-0 inset-y-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                </div>
              </div>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options className="absolute z-30 bg-white shadow-lg mt-1 py-1 rounded-md ring-1 ring-black/5 w-full max-h-60 text-xs overflow-auto focus:outline-none">
                  <div className="px-4 py-1.5 text-xs text-gray-400">
                    {query.length < MIN_SEARCH_CHARS
                      ? `Type at least ${MIN_SEARCH_CHARS} characters to search`
                      : <>Results for <span className="font-semibold text-blue-600">{query}</span></>
                    }
                  </div>
                  {query.length >= MIN_SEARCH_CHARS && searchResults.length === 0 ? (
                    <div className="px-4 py-2 text-xs text-gray-500">No results found.</div>
                  ) : (
                    searchResults.map((result, idx) => {
                      const isInTable = fields.some((f) => f.vetic_variant_id === result.vetic_variant_id);
                      return (
                        <Combobox.Option
                          key={`${result.vetic_variant_id}:${idx}`}
                          value={result}
                          disabled={isInTable}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 text-xs ${isInTable
                              ? "opacity-40 cursor-not-allowed text-gray-400"
                              : active
                                ? "bg-orange-500 text-white"
                                : "text-gray-900"
                            }`
                          }
                        >
                          {({ active }) => (
                            <>
                              <span className="block truncate text-xs">{result.variant_name}</span>
                              {isInTable && (
                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-orange-500"}`}>
                                  <CheckIcon className="w-4 h-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      );
                    })
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </Combobox>
        </div>

        {/* Variants table */}
        {fields.length > 0 && (
          <div className="rounded-xl mt-5 max-h-[500px] overflow-y-auto overflow-x-auto">
            <table className="w-full table-fixed border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
              <colgroup>
                <col className="w-[6%]" />
                <col className="w-[70%]" />
                <col className="w-[16%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead className="bg-[#F5F5F5] text-[#1F1F23] font-medium rounded-md sticky top-0 z-10">
                <tr>
                  <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">S.No</th>
                  <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Variant Name</th>
                  <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Quantity</th>
                  <th className="text-left text-[10px] sm:text-xs font-medium p-2 sm:p-3">Action</th>
                </tr>
              </thead>
              <tbody className="overflow-hidden rounded-xl w-full">
                {fields.map((item, index) => {
                  const isFirst = index === 0;
                  const isLast = index === fields.length - 1;
                  const isSingle = fields.length === 1;
                  return (
                    <tr key={item.id} className="bg-white overflow-hidden">
                      <td
                        className={`p-2 sm:p-3 text-xs text-[#1F1F23] ${isSingle ? "rounded-tl-xl rounded-bl-xl" : `${isFirst ? "rounded-tl-xl" : ""} ${isLast ? "rounded-bl-xl" : ""}`.trim()}`}
                      >
                        {index + 1}.
                      </td>
                      <td className="p-2 sm:p-3 text-xs text-[#1F1F23] font-medium">{item.variant_name}</td>
                      <td className="p-2 sm:p-3">
                        <Input
                          type="number"
                          placeholder="Qty"
                          className="w-24 shadow-none"
                          register={formHook.register(`variants.${index}.quantity`, {
                            required: true,
                            min: 1,
                            valueAsNumber: true,
                          })}
                          fieldError={formHook.formState.errors?.variants?.[index]?.quantity || null}
                          errorMessages={[
                            { type: "required", message: "Required" },
                            { type: "min", message: "Min 1" },
                          ]}
                        />
                      </td>
                      <td
                        className={`p-2 sm:p-2 text-center ${isSingle ? "rounded-tr-xl rounded-br-xl" : `${isFirst ? "rounded-tr-xl" : ""} ${isLast ? "rounded-br-xl" : ""}`.trim()}`}
                      >
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="w-4 h-4 flex items-center justify-center bg-[#F2F2F2] rounded-[10px]"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5 text-[#ED0101]">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Fixed bottom submit bar */}
        <div className="flex justify-end items-center gap-4 bottom-0 right-0 fixed bg-white border-t-2 border-gray-100 w-full p-3 z-10">
          <button
            type="submit"
            disabled={isSubmitting || fields.length === 0}
            className="text-sm inline-flex justify-center rounded-md bg-[#193A6A] px-10 py-3 font-semibold text-white hover:bg-[#162f58] transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
          >
            {isSubmitting ? "Submitting..." : "Submit PRF"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductRequirementForm;
